/* eslint-disable no-invalid-this */
import * as internals from './internals.js';
import debounce from 'lodash.debounce';

// I really don't like having this set of instances here, as it prevents
// instances from getting garbage collected. There's gotta be a way to keep
// track of this without keeping a set of all of the instances.
const featureFlagInstances = new Set();

/**
 * Root component for an application that uses feature flags.
 */
const FeatureFlagApp = {
    name: 'FeatureFlagApp',
    props: {
        /**
         * The environment name or URL to use in loading the feature flags.
         *
         * @type {('dev'|'test'|'prod'|string)}
         */
        env: {
            type: String,
            default: null
        },
        /**
         * The optional user ID to send to the feature flag server.
         */
        userId: {
            type: String,
            default: null
        }
    },
    provide() {
        return {
            [internals.FEATURE_ENABLED_KEY]: this.featureEnabled
        }
    },
    watch: {
        env: {
            immediate: true,
            handler() {
                this.loadFlags();
            }
        },
        userId: {
            immediate: true,
            handler() {
                this.loadFlags();
            }
        }
    },
    mounted() {
        registerInstanceAndLog(this, FeatureFlagApp.logMultipleInstances);
    },
    /* istanbul ignore next */
    unmounted() {
        unregisterInstance(this);
    },
    destroyed() {
        unregisterInstance(this);
    },
    data() {
        return {
            enabledFlags: new Set(),
            loaded: false
        };
    },
    methods: {
        // Debounce so that setting up the initial props state doesn't cause multiple calls
        loadFlags: debounce(async function() {
            this.loaded = false;

            let resultSet = new Set();
            try {
                resultSet = await internals.loadFlagsWithCache({env: this.env, userId: this.userId});
            } catch (err) {
                console.warn('Error loading feature flags; assuming all flags are disabled', err);
            } finally {
                this.enabledFlags = resultSet || new Set();
                this.loaded = true;
            }
        }, 50, {maxWait: 100}),
        /**
         * Check if a flag is enabled.
         * @param {string} flagName feature flag name
         * @returns {boolean} flag state
         */
        featureEnabled(flagName) {
            return this.enabledFlags.has(flagName);
        }
    },
    render(h) {
        if (this.loaded) {
            return this.$slots.default;
        } else {
            if (this.$slots.loading) {
                return this.$slots.loading;
            } else {
                return h('div', {class: 'feature-flag-app-loading'});
            }
        }
    }
};

/**
 * Set this to 'false' to disable warnings when there are multiple FeatureFlagApp elements in a single page.
 */
FeatureFlagApp.logMultipleInstances = internals.warningsEnabledByDefault;

export default FeatureFlagApp;

/* istanbul ignore next */
function registerInstanceAndLog(self, allowLogging) {
    if (featureFlagInstances.size !== 0 && allowLogging) {
        console.warn(`[feature-flag-app] Beware!
Having multiple feature-flag-app instances in an application can cause lots of problems.

This instance:
${internals.getComponentTree(self)}

Other instances:
${[...featureFlagInstances].map(internals.getComponentTree).join('\n')}
(to disable this warning, set FeatureFlagApp.logMultipleInstances = false)`)
    }
    featureFlagInstances.add(self);
}

/* istanbul ignore next */
function unregisterInstance(self) {
    featureFlagInstances.delete(self);
}
